import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import './AdminPage.css';

const AdminPage = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previousCount, setPreviousCount] = useState(0);
  const { toast } = useToast();

  const fetchInquiries = async (isAutoRefresh = false) => {
    try {
      if (!isAutoRefresh) {
        setLoading(true);
      }
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/inquiries/`);
      const newInquiries = response.data;
      
      // Check for new inquiries and show notification
      if (isAutoRefresh && previousCount > 0 && newInquiries.length > previousCount) {
        const newCount = newInquiries.length - previousCount;
        const latestInquiry = newInquiries[0]; // Newest inquiry (sorted by submittedAt desc)
        
        toast({
          title: '🎉 New Inquiry Received!',
          description: `${latestInquiry.name} just submitted an inquiry${newCount > 1 ? ` (+${newCount - 1} more)` : ''}`,
          duration: 8000,
        });
        
        // Optional: Play a notification sound
        try {
          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUKzn77RgGwY7k9n0y3krBSl+zPLaizsKGGS46+ufVBELTKXh8bVhGAU2jdXyz3YpBSh7yvHejz0LGV+16+6jVhEJTqnj77NgGQU7kdj0ynosBS1+zPDajT0KGmW56+yfUxEKTqvk8bNeGgY8lNr0ynkqBCp9y/HajD0JGWK36+6gVREKT6rj8LNfGgU8k9nzynYpBCp9y/HaizsKGWG36+6fVREKT6rk8LJeGgU8k9nzyncpBS1+y/Daiw==');
          audio.play().catch(() => {}); // Ignore if audio fails
        } catch (e) {
          // Ignore audio errors
        }
      }
      
      setInquiries(newInquiries);
      setPreviousCount(newInquiries.length);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
      if (!isAutoRefresh) {
        toast({
          title: 'Error',
          description: 'Failed to load inquiries',
          variant: 'destructive'
        });
      }
    } finally {
      if (!isAutoRefresh) {
        setLoading(false);
      }
    }
  };

  const handleDelete = async (inquiryId) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) {
      return;
    }

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.delete(`${BACKEND_URL}/api/inquiries/${inquiryId}`);
      toast({
        title: 'Success',
        description: 'Inquiry deleted successfully'
      });
      fetchInquiries(); // Refresh the list
    } catch (error) {
      console.error('Error deleting inquiry:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete inquiry',
        variant: 'destructive'
      });
    }
  };

  useEffect(() => {
    fetchInquiries();
    
    // Auto-refresh every 30 seconds to check for new inquiries
    const intervalId = setInterval(() => {
      fetchInquiries(true);
    }, 30000);
    
    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-header">
          <h1>Inquiry Management</h1>
        </div>
        <div className="loading-message">Loading inquiries...</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h1>Inquiry Management</h1>
          <p className="auto-refresh-text">Auto-refreshes every 30 seconds</p>
        </div>
        <Button onClick={() => fetchInquiries(false)} variant="outline">
          Refresh Now
        </Button>
      </div>

      <div className="inquiries-count">
        Total Inquiries: <strong>{inquiries.length}</strong>
      </div>

      {inquiries.length === 0 ? (
        <div className="no-inquiries">
          <p>No inquiries yet.</p>
        </div>
      ) : (
        <div className="inquiries-table-container">
          <table className="inquiries-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Journey</th>
                <th>Travel Month</th>
                <th>Guests</th>
                <th>Message</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {inquiries.map((inquiry) => (
                <tr key={inquiry.id}>
                  <td className="date-cell">{formatDate(inquiry.submittedAt)}</td>
                  <td className="name-cell">{inquiry.name}</td>
                  <td className="email-cell">{inquiry.email}</td>
                  <td className="phone-cell">{inquiry.phone}</td>
                  <td className="journey-cell">
                    {inquiry.itineraryName || inquiry.itineraryId || '-'}
                  </td>
                  <td className="travel-date-cell">{inquiry.travelMonth || '-'}</td>
                  <td className="guests-cell">{inquiry.numberOfGuests}</td>
                  <td className="message-cell">
                    {inquiry.message ? (
                      <div className="message-preview" title={inquiry.message}>
                        {inquiry.message.length > 50 
                          ? `${inquiry.message.substring(0, 50)}...` 
                          : inquiry.message}
                      </div>
                    ) : '-'}
                  </td>
                  <td className="status-cell">
                    <span className={`status-badge status-${inquiry.status}`}>
                      {inquiry.status}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <Button 
                      onClick={() => handleDelete(inquiry.id)} 
                      variant="destructive" 
                      size="sm"
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
